<script setup>
import axios from '@axios'
import moment from 'moment'
import { computed, ref, watch } from 'vue'

/*
 * RM-430 part G (RM-406) — an RM admin turns RM via text on for someone who
 * said yes to the words registered with Twilio: read to them on a call or in
 * person, or sent word for word from the admin's own phone or email (Kapil
 * 10/26: how restaurant contacts get on). The admin confirms the number is
 * theirs, that they used the words and got a yes, when, and what they said;
 * the server keeps the words with that consent record. Used for a member
 * (Platinum members tab) and a restaurant contact (Restaurant team tab).
 *
 * target: { kind: 'member' | 'contact', id?, phoneE164?, name, number }
 */
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  target: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue', 'done'])

const SOURCES = [
  { title: 'Text from my phone', value: 'text' },
  { title: 'Email', value: 'email' },
  { title: 'Phone call', value: 'phone' },
  { title: 'In person', value: 'in_person' },
]
// the admin's own calendar day (toISOString gives tomorrow's date in the evening)
const today = () => moment().format('YYYY-MM-DD')
const blankForm = () => ({ numberConfirmed: false, source: 'text', requestedOn: today(), note: '', scriptRead: false })
const form = ref(blankForm())
const saving = ref(false)
const error = ref('')
const script = ref('')
const copied = ref(false)

// On a call or in person the words are read aloud; by text or email they're sent.
const spoken = computed(() => ['phone', 'in_person'].includes(form.value.source))

async function copyScript() {
  try {
    await navigator.clipboard.writeText(script.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  }
  catch (e) {
    error.value = 'Couldn\'t copy. Select the words and copy them instead.'
  }
}

async function loadScript() {
  script.value = ''
  const t = props.target
  if (!t) return
  try {
    const params = t.kind === 'contact' ? { contact: t.phoneE164 } : { member: t.id }
    const { data } = await axios.get('/agent/admin/sms-verbal-script', { params })
    script.value = data.script
  }
  catch (e) {
    error.value = e?.response?.data?.error || 'Couldn\'t load the words to use.'
  }
}

watch(() => props.modelValue, open => {
  if (open) {
    form.value = blankForm()
    error.value = ''
    copied.value = false
    loadScript()
  }
})

const ready = computed(() => form.value.numberConfirmed && form.value.note.trim().length >= 3 && form.value.requestedOn
  && script.value && form.value.scriptRead)

async function submit() {
  if (!props.target) return
  saving.value = true
  error.value = ''
  try {
    const path = props.target.kind === 'contact'
      ? `/agent/admin/sms-restaurant-team/${encodeURIComponent(props.target.phoneE164)}/activate`
      : `/agent/admin/sms-members/${props.target.id}/activate`
    const body = { ...form.value, note: form.value.note.trim() }
    // a restaurant contact may have no texting record yet: send what the row shows
    if (props.target.kind === 'contact') Object.assign(body, { name: props.target.contactName || '', email: props.target.contactEmail || '' })
    const { data } = await axios.post(path, body)
    emit('done', data)
    emit('update:modelValue', false)
  }
  catch (e) {
    error.value = e?.response?.data?.error || 'Couldn\'t turn it on. Please try again.'
  }
  finally {
    saving.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="480"
    @update:model-value="v => emit('update:modelValue', v)"
  >
    <VCard v-if="target">
      <VCardTitle class="activate__title">
        Turn on RM via text for {{ target.name }}
      </VCardTitle>
      <VCardText>
        <p class="activate__lede">
          Ask in these exact words first. Once they say yes, RM texts them the usual welcome, and this is kept as their consent.
        </p>
        <VCheckbox
          v-model="form.numberConfirmed"
          :label="`${target.number} is ${target.name}'s own mobile`"
          hide-details
          density="comfortable"
          class="mb-2"
        />
        <VSelect
          v-model="form.source"
          :items="SOURCES"
          label="How you asked"
          variant="outlined"
          density="comfortable"
          class="activate__field mb-3"
          hide-details
        />
        <div class="activate__script mb-3">
          <div class="activate__script-head">
            <p class="activate__script-label">
              {{ spoken ? 'Read this to them, word for word' : 'Send them this, word for word' }}
            </p>
            <VBtn
              v-if="!spoken && script"
              variant="text"
              size="small"
              color="primary"
              class="px-1"
              @click="copyScript"
            >
              {{ copied ? 'Copied' : 'Copy' }}
            </VBtn>
          </div>
          <p
            v-if="script"
            class="activate__script-text"
          >
            “{{ script }}”
          </p>
          <VSkeletonLoader
            v-else
            type="text"
          />
          <VCheckbox
            v-model="form.scriptRead"
            :disabled="!script"
            :label="spoken ? 'I read this to them and they said yes' : 'I sent this and they replied yes'"
            hide-details
            density="comfortable"
          />
        </div>
        <VTextField
          v-model="form.requestedOn"
          type="date"
          label="When they said yes"
          :max="today()"
          variant="outlined"
          density="comfortable"
          class="activate__field mb-3"
          hide-details
        />
        <VTextarea
          v-model="form.note"
          :label="spoken ? 'What they said' : 'Their reply (paste it)'"
          variant="outlined"
          rows="3"
          auto-grow
          class="activate__field"
          hide-details
        />
        <VAlert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ error }}
        </VAlert>
      </VCardText>
      <VCardActions class="activate__actions">
        <VBtn
          variant="text"
          @click="emit('update:modelValue', false)"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          variant="flat"
          :disabled="!ready"
          :loading="saving"
          @click="submit"
        >
          Turn on
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.activate__title {
  font-size: 20px;
  font-weight: 700;
  white-space: normal;
}

.activate__lede {
  margin: 0 0 12px;
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

/* iOS zooms into inputs under 16px */
.activate__field :deep(input),
.activate__field :deep(textarea),
.activate__field :deep(.v-select__selection-text) {
  font-size: 16px;
}

.activate__script {
  padding: 12px 16px 4px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.activate__script-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 28px;
  margin: 0 0 4px;
}

.activate__script-label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.activate__script-text {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
}

.activate__actions {
  justify-content: flex-end;
  padding: 8px 16px 16px;
}
</style>
